import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function PricingManager() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/content/pricing").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/content/pricing", {
        name: "New package", price: "₹0", duration: "1 day", description: "",
        featured: false, order: items.length,
      });
      toast.success("Added"); load();
    } catch { toast.error("Could not add"); }
  };

  const save = async (p) => {
    try { await api.put(`/content/pricing/${p.id}`, p); toast.success("Saved"); }
    catch { toast.error("Could not save"); }
  };

  const del = async (id) => {
    try { await api.delete(`/content/pricing/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const upd = (id, field, val) => setItems(items.map((x) => x.id === id ? { ...x, [field]: val } : x));

  return (
    <div data-testid="pricing-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Manage</div>
          <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Pricing packages</h1>
        </div>
        <Button onClick={add} className="bg-sage-500 text-white hover:bg-sage-600 rounded-full" data-testid="add-pricing-btn">
          <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Add package
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-earth-100 p-6 flex flex-col gap-3" data-testid={`pricing-row-${p.id}`}>
            <Input value={p.name} onChange={(e) => upd(p.id, "name", e.target.value)} placeholder="Name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={p.price} onChange={(e) => upd(p.id, "price", e.target.value)} placeholder="Price" />
              <Input value={p.duration} onChange={(e) => upd(p.id, "duration", e.target.value)} placeholder="Duration" />
            </div>
            <Textarea rows={3} value={p.description || ""} onChange={(e) => upd(p.id, "description", e.target.value)} placeholder="Description" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Switch checked={!!p.featured} onCheckedChange={(v) => upd(p.id, "featured", v)} />
                <span className="text-earth-700">Featured</span>
              </div>
              <Input type="number" className="w-24" value={p.order} onChange={(e) => upd(p.id, "order", Number(e.target.value))} />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => save(p)} className="flex-1 bg-earth-900 text-white hover:bg-earth-800 rounded-full" data-testid={`pricing-save-${p.id}`}>
                <Save className="w-4 h-4 mr-1" strokeWidth={1.5} /> Save
              </Button>
              <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`pricing-del-${p.id}`}>
                <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
